import { useCallback } from "react";
import { Box, ButtonBase, Chip, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import HistoryIcon from "@mui/icons-material/History";

const tesoreriaAreas = [
  {
    icon: <AccountBalanceIcon />,
    label: "Lienzo de Trabajo",
    description: "Listado y gestión de cierres.",
    to: "/tesoreria/lienzo",
    disabled: false,
  },
  {
    icon: <HistoryIcon />,
    label: "Historial de Cierres",
    description: "Consulta histórica de cierres finalizados.",
    to: "/tesoreria/historial",
    disabled: false,
  },
  {
    icon: <DescriptionIcon />,
    label: "Formularios",
    description: "Gestión de plantillas y formularios.",
    to: "/tesoreria/formularios",
    disabled: false,
  },
];

function TesoreriaAccessCard({ area, onClick }) {
  const disabled = Boolean(area.disabled);

  return (
    <ButtonBase
      aria-disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      sx={{
        width: "100%",
        minHeight: { xs: 220, lg: 190 },
        borderRadius: "20px",
        alignItems: "stretch",
        justifyContent: "stretch",
        textAlign: "left",
        bgcolor: "rgba(255,255,255,0.78)",
        border: "1px solid rgba(255,255,255,0.92)",
        boxShadow:
          "0px 8px 20px rgba(27, 37, 89, 0.08), inset 0px 0px 3px rgba(142, 142, 159, 0.18)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.64 : 1,
        overflow: "hidden",
        position: "relative",
        transition:
          "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease",
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-6px)",
              bgcolor: "rgba(248, 250, 255, 0.96)",
              borderColor: "rgba(35, 78, 190, 0.35)",
              boxShadow:
                "0px 14px 30px rgba(27, 37, 89, 0.16), inset 0px 0px 3px rgba(142, 142, 159, 0.18)",
            },
        "&:hover .tesoreriaIcon": disabled
          ? {}
          : {
              transform: "scale(1.06) translateY(-2px)",
              bgcolor: "rgba(30, 68, 177, 0.13)",
            },
        "&:hover .tesoreriaTitle": disabled
          ? {}
          : {
              color: "primary.700",
              transform: "translateY(-2px)",
            },
        "&:hover .tesoreriaDescription": disabled
          ? {}
          : {
              opacity: 1,
              transform: "translateY(0)",
            },
        "&:hover .tesoreriaArrow": disabled
          ? {}
          : {
              bgcolor: "primary.600",
              color: "#fff",
              transform: "translateX(4px)",
              boxShadow: "0px 8px 18px rgba(16, 47, 148, 0.32)",
            },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {disabled ? (
          <Chip
            label={area.badge || "Próximamente"}
            size="small"
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              borderRadius: "10px",
              bgcolor: "neutral.200",
              color: "primary.900",
              fontSize: 10.5,
              fontWeight: 900,
            }}
          />
        ) : null}

        <Box
          className="tesoreriaIcon"
          sx={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            bgcolor: "rgba(30, 68, 177, 0.10)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.700",
            mb: 2,
            boxShadow: "inset 0px 0px 4px rgba(30, 68, 177, 0.08)",
            transition: "transform 180ms ease, background 180ms ease",
            "& svg": { width: 32, height: 32 },
          }}
        >
          {area.icon}
        </Box>

        <Typography
          className="tesoreriaTitle"
          sx={{
            fontSize: 16,
            fontWeight: 800,
            color: "rgba(18, 27, 61, 0.95)",
            lineHeight: 1,
            pr: disabled ? 9 : 0,
            transition: "transform 180ms ease, color 180ms ease",
          }}
        >
          {area.label}
        </Typography>

        <Typography
          className="tesoreriaDescription"
          sx={{
            mt: 1,
            fontSize: 13,
            fontWeight: 500,
            color: "rgba(19, 27, 58, 0.82)",
            lineHeight: 1.2,
            opacity: 0.9,
            transform: "translateY(2px)",
            transition: "opacity 180ms ease, transform 180ms ease",
          }}
        >
          {area.description}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0 }}>
          <Box
            className="tesoreriaArrow"
            sx={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              bgcolor: disabled ? "rgba(235,235,240,0.9)" : "rgba(255,255,255,0.9)",
              color: disabled ? "neutral.500" : "primary.800",
              border: "1px solid rgba(20, 32, 82, 0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition:
                "transform 180ms ease, background 180ms ease, color 180ms ease, box-shadow 180ms ease",
            }}
          >
            <ArrowForwardIcon sx={{ fontSize: 23 }} />
          </Box>
        </Box>
      </Box>
    </ButtonBase>
  );
}

export default function TesoreriaHomePage() {
  const navigate = useNavigate();

  const handleAreaClick = useCallback(
    (area) => {
      if (area?.disabled || !area?.to) return;
      navigate(area.to);
    },
    [navigate]
  );

  return (
    <Box
      sx={{
        width: "100%",
        pt: 2,
        pr: 3,
        pl: 0,
      }}
    >
      <Stack spacing={2}>
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            minHeight: { xs: "auto", md: "calc(100vh - 128px)" },
            bgcolor: "rgba(244, 245, 251, 0.88)",
            borderRadius: "24px",
            border: "1px solid rgba(255,255,255,0.8)",
            boxShadow:
              "inset 0px 0px 5px rgba(117, 117, 138, 0.25), 0px 10px 28px rgba(27, 37, 89, 0.08)",
          }}
        >
          <Stack spacing={2}>
            <Box
              sx={{
                width: "fit-content",
                px: 2,
                py: 1,
                bgcolor: "primary.500",
                borderRadius: "33px",
                boxShadow:
                  "inset 0px 0px 3px rgba(192, 192, 201, 0.5), 0px 5px 14px rgba(16, 47, 148, 0.28)",
              }}
            >
              <Typography
                sx={{
                  color: "logoblanco",
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                Áreas a acceder
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(5, minmax(0, 1fr))",
                },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {tesoreriaAreas.map((area) => (
                <TesoreriaAccessCard
                  key={area.label}
                  area={area}
                  onClick={() => handleAreaClick(area)}
                />
              ))}
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
